import { useCallback, useEffect, useRef, useState } from 'react';
import { notification } from 'antd';
import { useSelector } from 'react-redux';
import moment from 'moment';
import axios from 'axios';

import { IFonteACadastrar } from './interfaces';
import { stateGlobal } from '../../../interfaces/Global';

import { useGetColumnSearchPropsData } from '../../../hooks/FilterTable/date';
import { useGetColumnSearchProps } from '../../../hooks/FilterTable/stringCLearFiltros';
import { useGetColumnSearchPropsObj } from '../../../hooks/FilterTable/objString';
import { convertMoedaBRDoJSComExtamente2CasasDecimais } from '../../../services/Conversores';
import FontesACadastrarService from '../../../services/http/services/SaldoDeContas/FontesACadastrarService';
import { EmitirErro } from '../../../services/EmitirErro';

export const useFontesACadastrar = () => {
    const propsState = useSelector((state: stateGlobal) => state.global);

    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number>>([]);
    const [fontesACadastrar, setFontesACadastrar] = useState<IFonteACadastrar[]>([]);
    const [hasSelectedCadastrar, setHasSelectedCadastrar] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);
    const [loadingIgnorar, setLoadingIgnorar] = useState(false);
    const [fonteSelecionada, setFonteSelecionada] = useState<IFonteACadastrar | null>(null);

    const refContagemDoFiltro = useRef<Array<any>>([]);

    const signal = axios.CancelToken.source();
    const signal1 = axios.CancelToken.source();

    const buscaDadosFontesACadastrar = useCallback((): void => {
        setLoadingTable(true);

        const buscaDeDadosUsuarios = async () => {
            try {
                const data = await FontesACadastrarService.getFontesACadastrar(signal.token);

                setFontesACadastrar(data);
                setLoadingTable(false)
            } catch (error) {
                let msgErro: any = (error as Error);
                if (msgErro?.message != 'Requisicao cancelada!') {
                    setLoadingTable(false)
                    notification.error({
                        message: 'Erro',
                        description:
                            'Não foi possivel buscar os dados!',
                    });
                }
            }
        }

        buscaDeDadosUsuarios();
    }, []);

    useEffect(() => {
        buscaDadosFontesACadastrar();

        return () => {
            signal.cancel("Requisicao cancelada!");
            signal1.cancel("Requisicao cancelada!");
        }
    }, []);

    const clickBotaoCadastrarFonte = useCallback((): void => {
        const buscaDeDados = () => {

            if (selectedRowKeys.length == 1) {
                const conta = fontesACadastrar.find((e) => e.id === selectedRowKeys[0]);

                if (conta) {
                    setFonteSelecionada(conta);
                }
            }
        }

        buscaDeDados();
    }, [selectedRowKeys, fontesACadastrar]);

    const onclickTable = useCallback((record: IFonteACadastrar, row: any): void => {

        if (selectedRowKeys.includes(record.id)) {
            let filter = selectedRowKeys.filter((val) => {
                return val != record.id
            })
            if (filter.length === 1) {
                setSelectedRowKeys(filter);
                setHasSelectedCadastrar(true);
            } else if (filter.length === 0) {
                setSelectedRowKeys(filter);
                setHasSelectedCadastrar(false);
            } else {
                setSelectedRowKeys(filter);
            }

        } else {
            if (selectedRowKeys.length == 0) {
                setSelectedRowKeys([record.id]);
                setHasSelectedCadastrar(true);
            } else {
                setSelectedRowKeys([record.id]);
                setHasSelectedCadastrar(true);
            }
        }
    }, [selectedRowKeys]);

    const handleResetRowSelection = useCallback(() => {
        setSelectedRowKeys([]);
        setHasSelectedCadastrar(false);
    }, []);

    const handleFonteCreated = useCallback((_, fonteACadastrarId: number | undefined) => {
        setFontesACadastrar((prevState) => prevState.filter((conta) => conta.id !== fonteACadastrarId));
        handleResetRowSelection();
        window.dispatchEvent(new Event('atualizarContagemBalon'));
        handleCloseFonteForm();
    }, []);

    const onSelectChange = useCallback((selectedRowKeys): void => {
        if (selectedRowKeys.length > 1) {
            setSelectedRowKeys([selectedRowKeys[1]]);
            setHasSelectedCadastrar(true);
        } else if (selectedRowKeys.length === 0) {
            setSelectedRowKeys(selectedRowKeys);
            setHasSelectedCadastrar(false);
        } else {
            setSelectedRowKeys([selectedRowKeys[0]]);
            setHasSelectedCadastrar(true);
        }
    }, [selectedRowKeys]);

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnWidth: '1%',
        type: 'radio' as any
    };

    const refFuncaoLimparFiltrosStringNumero = useRef<any>(null);
    const columnSearchPropsNumero = useGetColumnSearchProps('numero', refFuncaoLimparFiltrosStringNumero);

    const columns = [
        {
            title: 'Órgão',
            dataIndex: 'orgao',
            ...useGetColumnSearchPropsObj('orgao', "nome"),
            width: '4%',
            render: (_: any, record: IFonteACadastrar) => record.orgao.nome,
        },
        {
            title: 'Número',
            dataIndex: 'numero',
            ...columnSearchPropsNumero,
            width: '4%',
            render: (numero: any) => numero,
        },
        {
            title: 'Empenho liquidado',
            dataIndex: 'valorEmpenhoLiquidado',
            ...columnSearchPropsNumero,
            width: '4%',
            sorter: (a: IFonteACadastrar, b: IFonteACadastrar) => (a.valorEmpenhoLiquidado || 0) - (b.valorEmpenhoLiquidado || 0),
            render: (valorEmpenhoLiquidado: number) => `R$ ${convertMoedaBRDoJSComExtamente2CasasDecimais(valorEmpenhoLiquidado, true)}`,
        },
        {
            title: 'Empenho não liquidado',
            dataIndex: 'valorEmpenhoNaoLiquidado',
            ...columnSearchPropsNumero,
            width: '4%',
            sorter: (a: IFonteACadastrar, b: IFonteACadastrar) => (a.valorEmpenhoNaoLiquidado || 0) - (b.valorEmpenhoNaoLiquidado || 0),
            render: (valorEmpenhoNaoLiquidado: number) => `R$ ${convertMoedaBRDoJSComExtamente2CasasDecimais(valorEmpenhoNaoLiquidado, true)}`,
        },

        {
            title: 'Resto liquidado',
            dataIndex: 'valorRestoLiquidado',
            ...columnSearchPropsNumero,
            width: '4%',
            sorter: (a: IFonteACadastrar, b: IFonteACadastrar) => (a.valorRestoLiquidado || 0) - (b.valorRestoLiquidado || 0),
            render: (valorRestoLiquidado: number) => `R$ ${convertMoedaBRDoJSComExtamente2CasasDecimais(valorRestoLiquidado, true)}`,
        },
        {
            title: 'Resto não liquidado',
            dataIndex: 'valorRestoNaoLiquidado',
            ...columnSearchPropsNumero,
            width: '4%',
            sorter: (a: IFonteACadastrar, b: IFonteACadastrar) => (a.valorRestoNaoLiquidado || 0) - (b.valorRestoNaoLiquidado || 0),
            render: (valorRestoNaoLiquidado: number) => `R$ ${convertMoedaBRDoJSComExtamente2CasasDecimais(valorRestoNaoLiquidado, true)}`,
        },
        {
            title: 'Data empenhos',
            dataIndex: 'dataEmpenho',
            ...useGetColumnSearchPropsData('dataEmpenho'),
            width: '4%',
            sorter: (a: IFonteACadastrar, b: IFonteACadastrar) => {

                let aMonMen: any = null
                let bMonMen: any = null
                if (a.dataEmpenho) {
                    aMonMen = moment(a.dataEmpenho).unix()
                } else {
                    aMonMen = 0
                }
                if (b.dataEmpenho) {
                    bMonMen = moment(b.dataEmpenho).unix()
                } else {
                    bMonMen = 0
                }

                return aMonMen - bMonMen
            },
            showSorterTooltip: false,
            render: (dataEmpenhos: any) => (
                dataEmpenhos === null ? '--' : moment(dataEmpenhos).format('DD/MM/YYYY HH:mm')
            ),
        },
        {
            title: 'Data restos',
            dataIndex: 'dataResto',
            ...useGetColumnSearchPropsData('dataResto'),
            width: '4%',
            sorter: (a: IFonteACadastrar, b: IFonteACadastrar) => {

                let aMonMen: any = null
                let bMonMen: any = null
                if (a.dataResto) {
                    aMonMen = moment(a.dataResto).unix()
                } else {
                    aMonMen = 0
                }
                if (b.dataResto) {
                    bMonMen = moment(b.dataResto).unix()
                } else {
                    bMonMen = 0
                }

                return aMonMen - bMonMen
            },
            showSorterTooltip: false,
            render: (dataRestos: any) => (
                dataRestos === null ? '--' : moment(dataRestos).format('DD/MM/YYYY HH:mm')
            ),
        },
        {
            title: 'Criado em',
            dataIndex: 'createdAt',
            ...useGetColumnSearchPropsData('createdAt'),
            width: '4%',
            sorter: (a: any, b: any) => {

                let aMonMen: any = null
                let bMonMen: any = null
                if (a.createdAt) {
                    aMonMen = moment(a.createdAt).unix()
                } else {
                    aMonMen = 0
                }
                if (b.createdAt) {
                    bMonMen = moment(b.createdAt).unix()
                } else {
                    bMonMen = 0
                }

                return aMonMen - bMonMen
            },
            showSorterTooltip: false,
            render: (criado: any) => (
                moment(criado).format('DD/MM/YYYY HH:mm')
            ),
        },
    ];

    const handleCloseFonteForm = useCallback(() => {
        setFonteSelecionada(null);
    }, []);

    const clickBotaoIgnorarFonte = useCallback(async (): Promise<void> => {
        if (selectedRowKeys.length !== 1) return;

        const fonte = fontesACadastrar.find((e) => e.id === selectedRowKeys[0]);

        if (!fonte) return;

        try {
            setLoadingIgnorar(true);

            await FontesACadastrarService.ignorarFonte({
                numero: fonte.numero,
                orgaoId: fonte.orgaoId,
            });

            setFontesACadastrar((prevState) => prevState.filter((item) => item.id !== fonte.id));
            handleResetRowSelection();

            window.dispatchEvent(new Event('atualizarContagemBalon'));

            notification.success({
                message: 'Sucesso',
                description: 'Fonte ignorada com sucesso!',
            });
        } catch (error) {
            EmitirErro(error);
        } finally {
            setLoadingIgnorar(false);
        }
    }, [selectedRowKeys, fontesACadastrar, handleResetRowSelection]);

    return {
        clickBotaoCadastrarFonte,
        clickBotaoIgnorarFonte,
        hasSelectedCadastrar,
        loadingIgnorar,
        loadingTable,
        onclickTable,
        rowSelection,
        fontesACadastrar,
        columns,
        refContagemDoFiltro,
        propsState,
        handleResetRowSelection,
        fonteSelecionada,
        handleCloseFonteForm,
        handleFonteCreated,
    }
}
